import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GridRowId, GridRowModel, GridRowParams, GridRowsLookup, gridRowsStateSelector, useGridApiRef, } from '@mui/x-data-grid-pro';
import Controls from '../Controls';
import { EditDialog, EditorModus, } from '../../components/EditDialog/EditDialog';
import { Box, Checkbox, CheckboxProps, Typography } from '@mui/material';
import { PlainSearchOptions, usePlainSearch } from '../../../utils/utilHooks';
import { TEntity } from '../../../../models/entity';
import { TreeRowsBuilderProps } from '../../../utils/treeUtils';
import { TreeDataGrid } from '../TreeView/TreeDataGrid';
import { ModalMultiSelectProps } from './ModalMultiSelect';
import { TreeMultiSelectCtxMenu } from './MultiSelectCtxMenu';
import { CheckBoxOutlinedIcon } from '../../controls/MaterialUIIcons';
import { EditPanelDataGrid } from '../../components/EditPanelDataGrid/EditPanelDataGrid';
import { useTheme } from '@mui/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useEditPanelContext } from '../../components/AdminDataGrid/EditPanelContext';
import { ButtonTypography, GreyOutlinedButton } from '../../components/Buttons/buttons';
import { useRememberSelectionModel } from '../../components/EditPanelDataGrid/editPanelDataGridHelper';
import SquareTwoToneIcon from '@mui/icons-material/SquareTwoTone';


export type TreeModalMultiSelectProps<T extends TEntity> = ModalMultiSelectProps<T> &
    (PlainSearchOptions<T>) &
    Omit<TreeRowsBuilderProps<T>, 'rows'> & {

        isOptionSelectable?: (params: T) => boolean;
        isButtonSok?: boolean;


    };
export const CheckboxGrid = React.forwardRef<HTMLButtonElement, CheckboxProps>(
    (props, ref) => {
        const theme = useTheme();
        if (props.disabled)
            return (
                <SquareTwoToneIcon style={{ color: theme.palette.grey.A400, height: '21.336px', width: '21.336px' }} />
            );
        return (
            <Checkbox
                ref={ref}
                {...props}
            />
        );
    }
);


export const checkBoxStyle = makeStyles((theme) => ({
    icon: {
        '& .MuiSvgIcon-root': {
            color: theme.palette.primary.main,
        }
    }
}));
export const useButtonSokStyle = makeStyles(() => ({
    buttonSearch: {
        height: '100%',
        display: 'flex',
        'padding-top': '12px',
        'padding-bottom': '8px'
    },
}));

function VisKunValgte(kunValgte: boolean, handleKunValgteChanged: (e: any, checked: boolean) => void) {
    const classes = checkBoxStyle();

    const theme = useTheme();
    return (
        <div style={{ color: theme.palette.primary.main, display: 'flex', alignItems: 'center' }}>
            <Checkbox
                sx={{
                    m: 0,
                    pt: 0,
                    height: 34,
                    pb: 0,
                    p: '4px',
                    pointerEvents: 'auto',
                }}
                className={classes.icon}
                name={'kunValgte'}
                checked={kunValgte ?? false}
                onChange={handleKunValgteChanged}
                checkedIcon={<CheckBoxOutlinedIcon />}
            />
            <Typography fontSize='0.875rem' fontWeight='bold'>{'Vis valgte'}</Typography>
        </div>
    );
}

export function TreeModalMultiSelect<T extends TEntity>(props: TreeModalMultiSelectProps<T>) {
    const {
        value: selected,
        columns,
        isOptionEqualToValue,
        options: inputOptions,
        isOpen,
        isButtonSok = true,
        plainSearch = true,
        title = 'Redigere',
        close,
        onChange,
        getOptions,
        ...others
    } = props;
    const apiRef = useGridApiRef();
    const [currentContextItem, setCurrentCtxItem] = useState<T>();
    const [kunValgte, setKunValgte] = useState(false);
    const isSelectedEqualToItem = useCallback((selected: T, item: T) => selected.id === item.id, []);
    const {
        syncRememberedSelections,
        rememberedSelectedValue,
        setRememberedSelectedValue,
        returnSelectionRememberModel,
        setIsSelected
    } = useRememberSelectionModel({ isSelectedEqualToItem });

    const theme = useTheme();
    const [pristine, setPristine] = useState(false);
    const isInitializedValueRef = React.useRef(false);
    const [options, setOptions] = useState<T[]>(inputOptions ?? []);
    const [optionsTree, setTreeOptions] = useState<T[]>(inputOptions ?? []);
    const [isTree, setIsTree] = useState(true);

    const getAllNode = props.getAllNode;
    useEffect(() => {
        if (isTree && props.getAllNode)
            props.getAllNode()
                .then((res) => {
                    setTreeOptions(res);
                });
    }, [isTree, getAllNode, setTreeOptions, props]);

    const getSearchOptions = useCallback(async (searchString: string) => {
        if (getOptions) {
            return await getOptions(searchString);
        } else {
            return Promise.resolve(inputOptions ?? []);
        }
    }, [getOptions, inputOptions]);

    const { loading, searchString, setSearchString } =
        usePlainSearch<T>({
            minimumSearchLength: 1,
            setOptions,
            getOptions: getSearchOptions,
            open: isOpen,
        });

    const saveClose = () => {
        const values = Array.from(rememberedSelectedValue.values()) as T[];
        onChange(values);
        close();
    };

    useEffect(() => {
        if (selected && isOpen && !loading && options.length > 0 && !isInitializedValueRef.current) {
            let equalValue = selected;
            if (isOptionEqualToValue) {
                equalValue = options.filter((option) =>
                    selected.some((value) => isOptionEqualToValue(option, value))
                );
            }
            setRememberedSelectedValue(prev => {
                const prevSet = new Set(prev);
                equalValue.forEach((selected) => {
                    if (Array.from(prev.values()).findIndex(i => i.id === selected.id) === -1) {
                        prevSet.add(selected);
                    }
                });
                return prevSet;
            });
            isInitializedValueRef.current = true;
        }
    }, [isOptionEqualToValue, isOpen, options, selected, loading, setRememberedSelectedValue]);

    useEffect(() => {
        const handleFocus = (event: FocusEvent) => {
            const focusedElement = event.target as HTMLElement;
            if (focusedElement && focusedElement.closest('[aria-hidden="true"]')) {
                focusedElement.blur();
            }
        };

        document.addEventListener('focusin', handleFocus);
        return () => {
            document.removeEventListener('focusin', handleFocus);
        };
    }, []);

    useEffect(() => {
        if (selected && isOpen && !loading && optionsTree.length > 0 && !isInitializedValueRef.current) {
            let equalValue = selected;
          
            if (isOptionEqualToValue) {
                equalValue = optionsTree.filter((option) =>
                    selected.some((value) => isOptionEqualToValue(option, value))
                );
            }
            setRememberedSelectedValue(prev => {
                const prevSet = new Set(prev);
                equalValue.forEach((selected) => {
                    if (Array.from(prev.values()).findIndex(i => i.id === selected.id) === -1) {
                        prevSet.add(selected);
                    }
                });
                return prevSet;
            });
            isInitializedValueRef.current = true;
        }
    }, [isOptionEqualToValue, isOpen, options, selected, loading, setRememberedSelectedValue, optionsTree]);


    function onInputChange(evt: any) {
        setSearchString(evt?.target?.value ?? '');
    }

    const handleKunValgteChanged = useCallback((e: any, checked: boolean) => {
        setKunValgte(!kunValgte);
        if (!kunValgte)
            setSearchString('');
    }, [kunValgte, setSearchString]);

    useEffect(() => {
        if (kunValgte || searchString?.length !== 0) {
            setIsTree(false);
        } else {
            setIsTree(true);
        }
    }, [kunValgte, searchString?.length]);

    const handleSelect = useCallback(() => {
        if (currentContextItem)
            setRememberedSelectedValue(prevState => {
                const prev = new Set(prevState);
                prev.add(currentContextItem);
                return prev;
            });
        setPristine(true);
    }, [currentContextItem, setRememberedSelectedValue]);

    const handleUnSelect = useCallback(() => {
        if (currentContextItem)
            setRememberedSelectedValue(prevState => {
                const prev = new Set(prevState);
                prev.delete(currentContextItem);
                return prev;
            });
        setPristine(true);
    }, [currentContextItem, setRememberedSelectedValue]);

    const handleSelectAllChildren = useCallback(() => {
        if (currentContextItem) {
            const rows = gridRowsStateSelector(apiRef.current.state).idRowsLookup  as GridRowsLookup;
            const ids = gridRowsStateSelector(apiRef.current.state).ids;

            ids.forEach((id) => {
                if (
                    (rows[id].path as string[]).includes(currentContextItem.id.toString()) ||
                    id === currentContextItem.id
                ) {
                    setRememberedSelectedValue(prevState => {
                        const prev = new Set(prevState);
                        prev.add(rows[id] as T);
                        return prev;
                    });
                    apiRef.current.setRowChildrenExpansion(id as GridRowId, true);
                }
            });
            setPristine(true);
        }
    }, [currentContextItem, apiRef, setRememberedSelectedValue]);

       const unSelectAllChildren = useCallback(() => {
        if (currentContextItem) {
            const selected = apiRef?.current?.getSelectedRows() ?? new Map<GridRowId, T>();
            const values = Array.from(selected.values()) as GridRowModel[];
    
            // Filter to keep only the current context item and its children
            const idsToUnselect = new Set(values
                .filter((row) => (row.path as string[]).includes(currentContextItem.id.toString()) || row.id === currentContextItem.id)
                .map(row => row.id));

            // Collapse the rows
            // itemsToUnselect.forEach((i) => {
            //     apiRef.current.setRowChildrenExpansion(i.id as GridRowId, false);
            // });
    
            // Update the state
            setPristine(true);
            setRememberedSelectedValue(prevState => {
                const nextState = new Set<T>();
                for(const item of prevState) {
                    if(!idsToUnselect.has(item.id)) {
                        nextState.add(item);
                    }
                }
                return nextState;
            });
    
            // Log for debugging
            console.log("Unselected items:", idsToUnselect);
        } else {
            console.error("currentContextItem is undefined");
        }
    }, [currentContextItem, apiRef, setRememberedSelectedValue]);
    

    const selectionModel = useMemo(
        () => returnSelectionRememberModel()
        , [returnSelectionRememberModel]);

    function isRowSelectable(params: GridRowParams<{ [key: string]: any }>) {
        return props.isOptionSelectable?.(params.row as T) ?? true;
    }

    function handleSearch() {
        setSearchString('');
    }

    const { validateField } = useEditPanelContext<TEntity>();
    const handleLoseFocus = useCallback(
        () => {
            if ((!validateField && !props.validateFunction) || !props.required) return;
            if (validateField) validateField(props.value, props.name);
            if (props.validateFunction) props.validateFunction(props.value, props.name);
        },
        [validateField, props]
    );
    const buttonStyle = useButtonSokStyle();

    const currentRows = useMemo(() => {
        if (isTree && !kunValgte)
            return optionsTree;
        if (kunValgte && isTree) {
            setIsTree(false);
            return Array.from(rememberedSelectedValue.values()).filter(i => options.some(j => j['id'] === i['id']));
        }
        if (kunValgte && !isTree)
            return Array.from(rememberedSelectedValue.values()).filter(i => optionsTree.some(j => j['id'] === i['id']));

        return options;
    }, [kunValgte, isTree, options, optionsTree, rememberedSelectedValue]);


    useEffect(() => {
        if (currentRows.length > 0) {
            syncRememberedSelections(currentRows ?? []);
        }
    }, [currentRows, syncRememberedSelections]);

    const handleSelectionModelChange = useCallback(
        (selections: T[]) => {
            setPristine(true);
            setRememberedSelectedValue((prevState) => {
                const prev = new Set(prevState);
                if (!isTree) {
                    prev.forEach((i: T) => {
                        if (searchString && searchString.length > 0) {

                            const selectedIds = new Set(selected.map(item => item.id));

                            for (const item of prev) {
                                if (!selectedIds.has(item.id)) {
                                    prev.delete(item);
                                }
                            }
                        } else {
                            if (Array.from(prev ?? []).some((item) => i.id === item.id) &&
                                !selections.some((selected) => selected.id === i.id)) {
                                prev.delete(i);
                            }
                        }
                    });
                }
                else {
                    prev.forEach((i: T) => {

                        if (Array.from(prev ?? []).some((item: T) => i.id === item.id) &&
                            !selections.some((selected) => selected.id === i.id)) {
                            prev.delete(i);
                        }
                    });
                }

                selections.forEach((selected) => {
                    if (Array.from(prev.values()).findIndex(i => i.id === selected.id) === -1) {
                        prev.add(selected);
                    }
                });
                setIsSelected(prev.size > 0);
                return prev;
            });
        },
        [setRememberedSelectedValue, isTree, setIsSelected, searchString, selected]
    );


    return (
        <EditDialog
            open={props.isOpen}
            editorModus={EditorModus.Opprett}
            canSave={pristine && !loading}
            onSave={saveClose}
            onCancel={() => {
                props.close();
                handleLoseFocus();
            }}
            maxWidth={'md'}
            width={856}
            height={726}
            fullWidth={false}
            title={title}
            testId={props.testId + '-select'}
            lagreBtnTitle={'Ok'}
        >
            <Box flexGrow={'1'} display='flex' flexDirection='column'>
                <Box display='flex' flexDirection='row' alignItems='center' gap={2} hidden={!plainSearch}>
                    <div style={{ flexGrow: 1 }}>
                        <Controls.TextField
                            label={'Søk'}
                            value={searchString ?? ''}
                            onChange={onInputChange}
                            name={'sok'}
                            disabled={kunValgte}
                            hidden={kunValgte}
                        />
                    </div>
                    {
                        isButtonSok && (<div className={buttonStyle.buttonSearch}>
                            <GreyOutlinedButton
                                hidden={kunValgte}
                                onClick={handleSearch}><ButtonTypography>Tøm</ButtonTypography></GreyOutlinedButton>
                        </div>)
                    }
                </Box>

                {isTree ? (
                    <TreeDataGrid
                        apiRef={apiRef}
                        // parentFieldID={others.parentFieldID}
                        // propertyId={others.propertyId}
                        // getChildrenItems={others.getChildrenItems}
                        // getAllNode={others.getAllNode}
                        {...others}
                        gridId={others.gridId + '_tree_modal_select'}
                        treeData={isTree}
                        headerLabel={`${title}${optionsTree?.length !== 0 ? ' (' + optionsTree?.length + ')' : ''}`}
                        disableColumnSettings={props.disableColumnSettings ?? true}
                        rows={currentRows}
                        defaultExpandAll={props.defaultExpandAll}
                        getCurrentItemCtxMenu={setCurrentCtxItem}
                        disableSelectionOnClick={false}
                        height='inherit'
                        className=''
                        loading={loading}
                        columns={columns}
                        hideFooterRowCount
                        hideFooter
                        autoHeight={false}
                        rowCount={optionsTree.length}
                        autoPageSize={true}
                        pagination={false}
                        checkboxSelection={true}
                        selectionModel={selectionModel}
                        saveInnstillings={true}
                        setSelectedRows={handleSelectionModelChange}
                        isRowSelectable={isRowSelectable}
                        density='compact'
                        contentContextMenu={
                            <TreeMultiSelectCtxMenu
                                select={handleSelect}
                                selectAllChildren={handleSelectAllChildren}
                                unSelect={handleUnSelect}
                                unSelectAllChildren={unSelectAllChildren}
                            />
                        }
                        components={{
                            BaseCheckbox: CheckboxGrid,
                        }}
                        componentsProps={{
                            toolbar: {
                                headerTextProps: {
                                    color: theme.palette.primary.main,
                                },
                                buttons: VisKunValgte(kunValgte, handleKunValgteChanged),
                            },
                        }}
                    />
                ) : (
                    <EditPanelDataGrid
                        searchMode
                        selectionModel={selectionModel}
                        headerLabel={`${title}${currentRows?.length !== 0 ? ' (' + currentRows?.length + ')' : ''}`}
                        setSelectedRows={handleSelectionModelChange}
                        disableSelectionOnClick={false}
                        isRowSelectable={isRowSelectable}
                        apiRef={apiRef}
                        gridId={others.gridId + '_tree_modal_select'}
                        checkboxSelection={true}
                        enableContextMeny={true}
                        getCurrentItemCtxMenu={setCurrentCtxItem}
                        loading={loading}
                        pagination={false}
                        hideFooter
                        autoHeight={false}
                        autoPageSize={true}
                        hideFooterRowCount
                        columns={[...props.columns]}
                        rows={currentRows}
                        components={{
                            BaseCheckbox: CheckboxGrid
                        }}
                        componentsProps={{
                            toolbar: {
                                headerTextProps: {
                                    color: theme.palette.primary.main,
                                },
                                buttons: VisKunValgte(kunValgte, handleKunValgteChanged),
                            },
                        }}
                    />
                )}
            </Box>
        </EditDialog>
    );
}
